import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Header from '../../Components/Header/Header';
import api from '../../api';
import BrandKitEditor, { cleanKit, kitProblem, sameKit, suggestLook } from './BrandKitEditor';
import LookMonitor from './LookMonitor';
import { ChannelCard, PlayVoice, VoiceAvatar, VoicePicker, channelVoiceInfo } from './VoiceParts';
import { VISUAL_MODE_INFO, errorMessage, fontCss, kitColors, loadSpeechifyVoices, newKey, loadDocChannels, loadDocOptions } from './docShared';
import './documentaries.css';
import './docwizard.css';

const STEPS = ['Script', 'Channel', 'Brand kit', 'Review'];
const DRAFT_KEY = 've-new-documentary-draft';

function readDraft() {
  try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || '{}') || {}; } catch (_) { return {}; }
}
function writeDraft(d) {
  try { localStorage.setItem(DRAFT_KEY, JSON.stringify(d)); } catch (_) { /* private mode */ }
}
function clearDraft() {
  try { localStorage.removeItem(DRAFT_KEY); } catch (_) { /* private mode */ }
}

function minutesLabel(words) {
  if (!words) return 'Nothing yet';
  const sec = Math.round((words / 150) * 60);
  if (sec < 60) return `About ${sec} seconds`;
  const m = Math.round(sec / 30) / 2;
  return `About ${m} minute${m === 1 ? '' : 's'}`;
}

// ── step 1 ───────────────────────────────────────────────────────────────────────────────────────────────────

function StepScript({ title, setTitle, script, setScript, limits }) {
  const fileRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const chars = script.trim().length;
  const words = script.trim() ? script.trim().split(/\s+/).length : 0;
  const min = limits.min_script_chars || 0;
  const max = limits.max_script_chars || 20000;
  const pct = Math.min(100, (chars / max) * 100);
  const state = !chars ? 'empty' : chars < min ? 'short' : chars > max ? 'long' : 'ok';

  const readFile = (f) => {
    if (!f) return;
    if (f.size > 400 * 1024) { toast.error('That file is too big for a script.'); return; }
    if (!/\.(txt|md)$/i.test(f.name) && f.type && !f.type.startsWith('text/')) {
      toast.error('Upload a plain text file (.txt).'); return;
    }
    f.text().then((t) => {
      setScript(t.trim());
      if (!title.trim()) setTitle(f.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').slice(0, 120));
      toast.success(`Loaded ${f.name}`);
    });
  };

  return (
    <div className="wz-script">
      <div className="wz-script__main">
        <label className="wz-label" htmlFor="wz-title">Title</label>
        <input id="wz-title" className="wz-title-input" value={title} maxLength={140} autoFocus
               placeholder="The Night the Banks Closed" onChange={(e) => setTitle(e.target.value)} />

        <div
          className={`wz-editor ${drag ? 'is-drag' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); readFile(e.dataTransfer.files?.[0]); }}
        >
          <div className="wz-editor__bar">
            <label className="wz-label" htmlFor="wz-script">Narration script</label>
            <button type="button" className="docs-link" onClick={() => fileRef.current?.click()}>Upload a .txt file</button>
            <input ref={fileRef} type="file" accept=".txt,.md,text/plain" hidden
                   onChange={(e) => { readFile(e.target.files?.[0]); e.target.value = ''; }} />
          </div>
          <textarea id="wz-script" className="wz-editor__text" value={script} spellCheck
                    placeholder="Paste exactly what the narrator says, or drop a text file here."
                    onChange={(e) => setScript(e.target.value)} />
          {drag && <div className="wz-editor__drop">Drop the text file to load it</div>}
        </div>
      </div>

      <aside className="wz-script__side" aria-live="polite">
        <div className="wz-stat">
          <span className="wz-stat__label">Voice length</span>
          <span className="wz-stat__value">{minutesLabel(words)}</span>
        </div>
        <div className="wz-stat">
          <span className="wz-stat__label">Words</span>
          <span className="wz-stat__value">{words.toLocaleString()}</span>
        </div>
        <div className="wz-stat">
          <span className="wz-stat__label">Characters</span>
          <span className="wz-stat__value">{chars.toLocaleString()} <small>of {max.toLocaleString()}</small></span>
          <span className={`wz-meter wz-meter--${state}`}><span style={{ width: `${pct}%` }} /></span>
          <span className={`wz-stat__hint wz-stat__hint--${state}`}>
            {state === 'empty' && `Between ${min.toLocaleString()} and ${max.toLocaleString()} characters.`}
            {state === 'short' && `${(min - chars).toLocaleString()} more characters needed.`}
            {state === 'long' && `${(chars - max).toLocaleString()} characters over the limit.`}
            {state === 'ok' && 'Good length.'}
          </span>
        </div>
        <p className="wz-aside-note">
          Write the script exactly as it should be spoken. Every picture in the video is timed to these words.
        </p>
      </aside>
    </div>
  );
}

// ── step 2 ───────────────────────────────────────────────────────────────────────────────────────────────────

function StepChannel({ channels, channelId, pickChannel, kits, setKitFor, voices, options }) {
  const [picking, setPicking] = useState(false);
  const ch = channels?.find((c) => c.id === channelId);
  const kit = ch ? kits[ch.id] || {} : {};
  const info = ch ? channelVoiceInfo(ch, kit, voices, options?.voices) : null;
  const details = options?.look_details || {};

  useEffect(() => { setPicking(false); }, [channelId]);

  if (!channels) return <div className="wz-loading">Loading your channels…</div>;
  if (channels.length === 0) {
    return (
      <div className="wz-empty">
        <h3>Create a channel first</h3>
        <p>Each documentary is made for a channel: its voice and brand kit come from there.</p>
        <Link className="docs-btn docs-btn--primary" to="/channels">Go to Channels</Link>
      </div>
    );
  }

  return (
    <div className="wz-channel">
      <div className="dch-grid" role="radiogroup" aria-label="Channel">
        {channels.map((c) => (
          <ChannelCard key={c.id} ch={c} kit={kits[c.id] || c.brand_kit} active={c.id === channelId}
                       onPick={() => pickChannel(c.id)}
                       info={channelVoiceInfo(c, kits[c.id] || c.brand_kit, voices, options?.voices)}
                       lookTitle={details[(kits[c.id] || c.brand_kit || {}).look]?.title} />
        ))}
      </div>

      {ch && (
        <section className="wz-voice">
          <div className="wz-voice__now">
            <VoiceAvatar voice={info.effectiveObj} name={info.effective?.name} />
            <div className="wz-voice__text">
              {info.effective ? (
                <>
                  <strong>{info.effective.name}</strong> narrates this documentary.
                  <span>
                    {info.override
                      ? (info.ownUsable ? ` Chosen for documentaries instead of the channel voice (${info.ownName}).`
                        : ' Chosen for documentaries on this channel.')
                      : ` This is ${ch.name}'s voice from the Voices page.`}
                  </span>
                </>
              ) : info.own ? (
                <>
                  <strong>The channel voice can't narrate documentaries.</strong>
                  <span> {ch.name} uses {info.ownName.startsWith('a ') ? info.ownName : `${info.ownName}, which is not a Speechify or Inworld voice`}.
                    Documentaries need Speechify or Inworld to time every picture to the words. Choose a voice below; it is saved
                    with this channel's brand kit.</span>
                </>
              ) : (
                <>
                  <strong>{ch.name} has no voice yet.</strong>
                  <span> Choose a Speechify or Inworld voice below; it is saved with this channel's brand kit.</span>
                </>
              )}
            </div>
            {info.effective && <PlayVoice id={`now-${ch.id}`} voice={info.effectiveObj} label={info.effective.name} />}
            <div className="wz-voice__actions">
              {info.override && info.ownUsable && (
                <button type="button" className="docs-btn docs-btn--ghost"
                        onClick={() => setKitFor(ch.id, { ...kit, voice: '', voice_name: '' })}>
                  Use the channel voice
                </button>
              )}
              <button type="button" className="docs-btn" onClick={() => setPicking((p) => !p)}>
                {picking ? 'Close' : info.effective ? 'Use a different voice' : 'Choose a voice'}
              </button>
            </div>
          </div>
          {(picking || !info.effective) && (
            <VoicePicker voices={voices} selected={info.effective?.id}
                         onPick={(v) => { setKitFor(ch.id, { ...kit, voice: v.id, voice_name: v.display_name }); setPicking(false); }} />
          )}
        </section>
      )}
    </div>
  );
}

// ── step 4 ───────────────────────────────────────────────────────────────────────────────────────────────────

function StepReview({ title, script, ch, kit, info, options, saveKit, setSaveKit, kitDirty, goTo }) {
  const look = options?.look_details?.[kit.look];
  const c = kitColors(look, kit);
  const words = script.trim().split(/\s+/).length;
  return (
    <div className="wz-review">
      <div className="wz-review__preview">
        {look && <LookMonitor lookName={kit.look} look={look} kit={kit} title={title} script={script}
                              channelName={ch?.name} />}
      </div>
      <div className="wz-review__list">
        <div className="wz-sum">
          <div className="wz-sum__head"><h3>Script</h3><button type="button" className="docs-link" onClick={() => goTo(0)}>Edit</button></div>
          <p className="wz-sum__title">{title}</p>
          <p className="wz-sum__meta">{words.toLocaleString()} words, {minutesLabel(words).toLowerCase()} of voice</p>
          <p className="wz-sum__excerpt">{script.trim().slice(0, 220)}{script.trim().length > 220 ? '…' : ''}</p>
        </div>
        <div className="wz-sum">
          <div className="wz-sum__head"><h3>Channel and voice</h3><button type="button" className="docs-link" onClick={() => goTo(1)}>Edit</button></div>
          <div className="wz-sum__voice">
            <VoiceAvatar voice={info?.effectiveObj} name={info?.effective?.name} />
            <span className="wz-sum__who"><strong>{ch?.name}</strong><span>Narrated by {info?.effective?.name}</span></span>
            <PlayVoice id="review" voice={info?.effectiveObj} label={info?.effective?.name} />
          </div>
        </div>
        <div className="wz-sum">
          <div className="wz-sum__head"><h3>Brand kit</h3><button type="button" className="docs-link" onClick={() => goTo(2)}>Edit</button></div>
          {look && (
            <>
              <p className="wz-sum__title" style={{ ...fontCss(look.fonts.display), textTransform: 'none', letterSpacing: 0 }}>{look.title}</p>
              <div className="wz-sum__swatches">
                {[c.background, c.on_footage, c.accent, c.accent2].map((col, i) => <span key={i} style={{ background: col }} />)}
              </div>
              <p className="wz-sum__meta">
                {VISUAL_MODE_INFO[kit.visual_mode || 'footage']?.title}
                {kit.genre ? `, ${options.genres?.[kit.genre] || kit.genre}` : ''}
                {kit.avatar === true ? ', with a talking avatar' : ''}
              </p>
            </>
          )}
          {kitDirty && (
            <label className="docs-check">
              <input type="checkbox" checked={saveKit} onChange={(e) => setSaveKit(e.target.checked)} />
              Save this brand kit to {ch?.name}
            </label>
          )}
        </div>
        <ol className="wz-next">
          <li><strong>Voice and timings.</strong> Made in a few minutes. Telegram tells you when they're ready.</li>
          <li><strong>Plan with Claude.</strong> Copy the timings and brand kit to Claude, paste back its plan.</li>
          <li><strong>Render.</strong> The finished video appears on the project page.</li>
        </ol>
      </div>
    </div>
  );
}

// ── the wizard ───────────────────────────────────────────────────────────────────────────────────────────────

function NewDocumentary() {
  const navigate = useNavigate();
  const draft = useMemo(readDraft, []);
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState('fwd');
  const [options, setOptions] = useState(null);
  const [channels, setChannels] = useState(null);
  const [voices, setVoices] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [title, setTitle] = useState(draft.title || '');
  const [script, setScript] = useState(draft.script || '');
  const [channelId, setChannelId] = useState(draft.channelId || '');
  const [kits, setKits] = useState({}); // channel id → kit being edited
  const [saveKit, setSaveKit] = useState(true);
  const [busy, setBusy] = useState(false);
  const keyRef = useRef(newKey());
  const topRef = useRef(null);

  useEffect(() => {
    Promise.all([loadDocOptions(), loadDocChannels(true)])
      .then(([o, list]) => {
        setOptions(o);
        setChannels(list);
        const k = {};
        list.forEach((ch) => { k[ch.id] = { ...(ch.brand_kit || {}) }; });
        setKits(k);
        if (list.length === 1 && !draft.channelId) setChannelId(list[0].id);
      })
      .catch((e) => setLoadError(errorMessage(e, 'Could not load the form.')));
    loadSpeechifyVoices().then((v) => setVoices(v || []));
  }, [draft.channelId]);

  useEffect(() => { writeDraft({ title, script, channelId }); }, [title, script, channelId]);

  const limits = useMemo(() => options?.limits || {}, [options]);
  const ch = channels?.find((c) => c.id === channelId);
  const kit = useMemo(() => {
    const k = (ch && kits[ch.id]) || {};
    if (ch && !k.look && options?.look_details) return { ...k, look: suggestLook(ch.niche, options.look_details) };
    return k;
  }, [ch, kits, options]);
  const info = ch ? channelVoiceInfo(ch, kit, voices, options?.voices) : null;
  const kitDirty = ch ? !sameKit(ch.brand_kit || {}, kit) : false;

  const setKitFor = useCallback((id, k) => setKits((p) => ({ ...p, [id]: k })), []);
  const onSaved = (updated) => {
    setChannels((list) => list.map((c) => (c.id === updated.id ? { ...c, brand_kit: updated.brand_kit } : c)));
  };

  const chars = script.trim().length;
  const valid = [
    title.trim().length >= 3 && chars >= (limits.min_script_chars || 1) && chars <= (limits.max_script_chars || 1e9),
    Boolean(ch && info?.effective),
    Boolean(kit.look) && !kitProblem(kit, options?.look_details?.[kit.look]),
    true,
  ];
  const why = [
    title.trim().length < 3 ? 'Give the documentary a title.' : 'The script length is outside the limits.',
    !ch ? 'Choose a channel.' : 'Choose a voice for this channel.',
    kitProblem(kit, options?.look_details?.[kit.look]) || 'Choose a look.',
    '',
  ][step];

  const goTo = (n) => {
    if (n > step && !valid.slice(0, n).every(Boolean)) return;
    setDir(n > step ? 'fwd' : 'back');
    setStep(n);
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const submit = () => {
    if (busy || !valid.every(Boolean)) return;
    setBusy(true);
    const k = cleanKit(kit);
    const body = {
      title: title.trim(), script, channel_id: ch.id, save_brand_kit: kitDirty && saveKit,
      voice: info.effective.id, voice_name: info.effective.name,
      look: k.look, genre: k.genre || '', visual_mode: k.visual_mode || 'footage', accent: k.accent || '',
      background: k.background || '', card: k.card || '', panel: k.panel || '',
      brand_notes: k.notes || '',
      avatar: k.avatar === true, // optional presenter: off unless switched on in the brand kit
    };
    api.post('/visual-engine/videos', body, { headers: { 'Idempotency-Key': keyRef.current } })
      .then((r) => {
        clearDraft();
        toast.success('Started. Telegram will tell you when the timings are ready.');
        navigate(`/documentaries/${r.data.video.id}`);
      })
      .catch((err) => { toast.error(errorMessage(err)); setBusy(false); });
  };

  const nextLabel = ['Choose the channel', 'Set the brand kit', 'Review', busy ? 'Starting…' : 'Make voice and timings'][step];

  return (
    <div className="docs-page wz-page">
      <Header />
      <main className="wz-main" ref={topRef}>
        <div className="wz-head">
          <Link to="/documentaries" className="docs-back">Documentaries</Link>
          <h1>New documentary</h1>
        </div>

        <ol className="wz-rail" aria-label="Steps">
          {STEPS.map((s, i) => {
            const state = i < step ? 'done' : i === step ? 'now' : 'later';
            const reachable = i <= step || valid.slice(0, i).every(Boolean);
            return (
              <li key={s} className={`wz-rail__step is-${state}`}>
                <button type="button" disabled={!reachable} onClick={() => goTo(i)}
                        aria-current={i === step ? 'step' : undefined}>
                  <span className="wz-rail__num">{i < step ? '✓' : i + 1}</span>
                  <span className="wz-rail__name">{s}</span>
                </button>
              </li>
            );
          })}
        </ol>

        {loadError && <div className="docs-alert docs-alert--bad">{loadError}</div>}

        <div className={`wz-body wz-body--${dir}`} key={step}>
          <div className="wz-body__intro">
            <h2>{[
              'What is the narrator going to say?',
              'Which channel is this for?',
              `How should ${ch?.name || 'the channel'} look?`,
              'Ready to make the voice',
            ][step]}</h2>
            <p>{[
              'Paste the finished script. The voice is made from exactly this text.',
              "The channel brings its voice and its brand kit. Pick one and listen to the voice.",
              'Pick a look and an accent colour. The preview shows real titles, captions and numbers in this style.',
              'Check everything once. You can still change the brand kit when the plan is ready.',
            ][step]}</p>
          </div>

          {step === 0 && <StepScript title={title} setTitle={setTitle} script={script} setScript={setScript} limits={limits} />}
          {step === 1 && <StepChannel channels={channels} channelId={channelId} pickChannel={setChannelId} kits={kits}
                                      setKitFor={setKitFor} voices={voices} options={options} />}
          {step === 2 && options && ch && (
            <BrandKitEditor options={options} kit={kit} onChange={(k) => setKitFor(ch.id, k)} channel={ch}
                            title={title} script={script} onSaved={onSaved} />
          )}
          {step === 3 && <StepReview title={title} script={script} ch={ch} kit={kit} info={info} options={options}
                                     saveKit={saveKit} setSaveKit={setSaveKit} kitDirty={kitDirty} goTo={goTo} />}
        </div>
      </main>

      <footer className="wz-foot">
        <div className="wz-foot__in">
          <button type="button" className="docs-btn docs-btn--ghost" disabled={step === 0} onClick={() => goTo(step - 1)}>
            Back
          </button>
          <span className="wz-foot__why">{!valid[step] ? why : ''}</span>
          <button type="button" className="docs-btn docs-btn--primary docs-btn--big"
                  disabled={!valid[step] || busy || (step === 3 && !valid.every(Boolean))}
                  onClick={() => (step === 3 ? submit() : goTo(step + 1))}>
            {nextLabel}
          </button>
        </div>
      </footer>
    </div>
  );
}

export default NewDocumentary;
